import { type FC, useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Button, Radio, Space, Spin } from 'antd';

import { getSubscription } from 'queries';

type Props = {
  subscriptionId?: number;
  open: boolean;
  onSave: (email_invoice: 0 | 1) => void;
  isSaving?: boolean;
};

// email_invoice may arrive as 0|1 or as "0"/"1" from the API; normalise to a number.
const normalise = (raw: unknown): 0 | 1 | undefined =>
  raw === undefined || raw === null ? undefined : ((Number(raw) ? 1 : 0) as 0 | 1);

export const EmailInvoiceSubmenu: FC<Props> = ({ subscriptionId, open, onSave, isSaving }) => {
  const { data, isLoading, refetch } = useQuery(['subscription', subscriptionId], getSubscription, {
    enabled: !!subscriptionId,
  });

  const current = normalise(data?.email_invoice);
  const [selected, setSelected] = useState<0 | 1 | undefined>(undefined);

  useEffect(() => {
    // Adopt the fetched value only on initial load; don't clobber a selection the user
    // has already changed if the query refetches in the background (e.g. on window focus).
    setSelected((prev) => (prev === undefined && current !== undefined ? current : prev));
  }, [current]);

  useEffect(() => {
    if (!open) {
      // Discard an unsaved pick when the submenu closes, so a reopen shows the real value
      // rather than what was abandoned.
      setSelected(undefined);
      return;
    }

    // A reopen always asks the server instead of trusting a cache nothing else invalidates,
    // and adopts directly from this fetch's own resolved value rather than waiting for the
    // `current` effect above to notice a change — if the refetch resolves to the *same* value
    // as before, `current` wouldn't change, and that effect wouldn't otherwise re-run, leaving
    // `selected` stuck at the `undefined` this effect just reset it to on close.
    let cancelled = false;
    refetch().then((result) => {
      if (cancelled) {
        return;
      }
      const fresh = normalise(result.data?.email_invoice);
      setSelected((prev) => (prev === undefined && fresh !== undefined ? fresh : prev));
    });

    return () => {
      cancelled = true;
    };
  }, [open, refetch]);

  if (isLoading || current === undefined || selected === undefined) {
    return <Spin size="small" />;
  }

  const changed = selected !== current;

  return (
    <Space direction="vertical" size="small" onClick={(event) => event.stopPropagation()}>
      <Radio.Group value={selected} onChange={(event) => setSelected(event.target.value)}>
        <Radio value={1}>Yes</Radio>
        <Radio value={0}>No</Radio>
      </Radio.Group>
      <Button size="small" type="primary" disabled={!changed} loading={isSaving} onClick={() => onSave(selected)}>
        Save
      </Button>
    </Space>
  );
};
